import React, { useState, useEffect } from "react";
import { Plus, Search, Trash2, X, Mail, Users, Send } from "lucide-react";
import api from "../../services/api";
import toast from "react-hot-toast";

interface PromotionalEmailData {
  _id: string;
  subject: string;
  content: string;
  recipientCount: number;
  sentAt?: string;
  status: "pending" | "sent" | "failed";
  createdAt: string;
}

const PromotionalEmail: React.FC = () => {
  const [emails, setEmails] = useState<PromotionalEmailData[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
    content: "",
  });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEmails, setTotalEmails] = useState(0);
  const [subscribersCount, setSubscribersCount] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchEmails();
    fetchSubscribersCount();
  }, [currentPage]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(
        `/promotional-emails?page=${currentPage}&limit=${itemsPerPage}`
      );
      if (response.data.success) {
        setEmails(response.data.data.data);
        setTotalEmails(response.data.data.total);
      }
    } catch {
      setError("Failed to load promotional emails. Please try again.");
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribersCount = async () => {
    try {
      const response = await api.get("/promotional-emails/subscribers-count");
      if (response.data.success) {
        setSubscribersCount(response.data.data.count);
      }
    } catch {
      console.error("Failed to fetch subscribers count");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.subject.trim()) {
      toast.error("Subject is required");
      return;
    }

    if (!formData.content.trim()) {
      toast.error("Content is required");
      return;
    }

    if (subscribersCount === 0) {
      toast.error("No subscribers available to send emails");
      return;
    }

    try {
      setSending(true);
      setError(null);

      const response = await api.post("/promotional-emails/send", {
        subject: formData.subject,
        content: formData.content,
      });

      if (response.data.success) {
        toast.success(response.data.message);
        await fetchEmails();
        resetForm();
        setShowModal(false);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to send promotional email. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setFormData({
      subject: "",
      content: "",
    });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this email record?")) {
      try {
        setError(null);
        await api.delete(`/promotional-emails/${id}`);
        await fetchEmails();
        toast.success("Email record deleted successfully!");
      } catch {
        setError("Failed to delete email record. Please try again.");
        toast.error("Failed to delete email record.");
      }
    }
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const filteredEmails = emails.filter((email) =>
    email.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(totalEmails / itemsPerPage);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "sent":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  if (loading && emails.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto p-4 pt-0 lg:p-8 flex flex-col overflow-y-auto h-screen">
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Promotional Emails
            </h1>
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
              <Users size={14} />
              {subscribersCount} subscribed users
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <button
              onClick={openAddModal}
              className="w-auto self-start sm:self-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition inline-flex items-center gap-2 whitespace-nowrap" >
              <Plus size={18} />
              Create Email
            </button>
          </div>
        </div>

        <div className="rounded-lg mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search Emails
          </label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by subject..."
              className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">
          {error}
        </div>
      )}

      <div className="flex-1">
        {filteredEmails.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <Mail size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No promotional emails found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different term"
                  : "Create your first promotional email to get started"}
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full bg-white border rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Subject
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Recipients
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Sent At
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredEmails.map((email) => (
                  <tr key={email._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 line-clamp-1">
                          {email.subject}
                        </p>
                        <p className="text-sm text-gray-500 line-clamp-1">
                          {email.content.replace(/<[^>]*>/g, "").slice(0, 50)}
                          ...
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {email.recipientCount}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold capitalize ${getStatusBadge(
                          email.status
                        )}`}
                      >
                        {email.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-sm">
                      {email.sentAt
                        ? new Date(email.sentAt).toLocaleString()
                        : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDelete(email._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalEmails > 0 && totalPages > 1 && (
        <div className="py-4 flex justify-center">
          <div className="flex gap-2 items-center">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 rounded transition ${
                  currentPage === page
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => !sending && setShowModal(false)}
          />

          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col mx-auto">
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800 mb-0">
                Create Promotional Email
              </h2>
              <button
                type="button"
                onClick={() => !sending && setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={sending}
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <form id="email-form" onSubmit={handleSubmit} className="p-6">
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md flex items-center gap-3">
                    <Users className="text-blue-600" size={24} />
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        This email will be sent to {subscribersCount} subscribed
                        users
                      </p>
                      <p className="text-xs text-blue-700">
                        Only users who opted in for promotional emails will
                        receive this
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      type="text"
                      placeholder="Enter email subject..."
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      required
                      disabled={sending}
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Content <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      rows={8}
                      placeholder="Write your promotional email content here... You can use HTML for formatting."
                      value={formData.content}
                      onChange={(e) =>
                        setFormData({ ...formData, content: e.target.value })
                      }
                      required
                      disabled={sending}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Tip: You can use HTML tags like &lt;b&gt;, &lt;p&gt;,
                      &lt;br&gt; for formatting
                    </p>
                  </div>
                </div>
              </form>
            </div>

            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                disabled={sending}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="email-form"
                className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50"
                disabled={sending || subscribersCount === 0}
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionalEmail;
